// console.* is intentionally allowed in this file only.
// All other code in apps/web/src must use this logger, not console directly.
// ESLint override is defined in eslint.config.mjs.

type LogLevel = 'debug' | 'info' | 'warn' | 'error';

interface LogEntry {
  level: LogLevel;
  message: string;
  data?: unknown;
  timestamp: string;
}

const isDev = import.meta.env.DEV;

function formatMessage(level: LogLevel, message: string, data?: unknown): string {
  const prefix = `[${level.toUpperCase()}]`;
  return data !== undefined ? `${prefix} ${message}` : `${prefix} ${message}`;
}

const pendingLogs: LogEntry[] = [];

function log(level: LogLevel, message: string, data?: unknown): void {
  const entry: LogEntry = {
    level,
    message,
    data,
    timestamp: new Date().toISOString(),
  };

  if (isDev) {
    const formatted = formatMessage(level, message, data);
    switch (level) {
      case 'debug':
        console.debug(formatted, ...(data !== undefined ? [data] : []));
        break;
      case 'info':
        console.info(formatted, ...(data !== undefined ? [data] : []));
        break;
      case 'warn':
        console.warn(formatted, ...(data !== undefined ? [data] : []));
        break;
      case 'error':
        console.error(formatted, ...(data !== undefined ? [data] : []));
        break;
    }
  } else {
    pendingLogs.push(entry);
    // In production, logs are POSTed to the backend log endpoint (Prompt 8).
    // Batching prevents flooding on high-frequency events.
    if (pendingLogs.length >= 10) {
      flushLogs();
    }
  }
}

function flushLogs(): void {
  if (pendingLogs.length === 0) return;
  const batch = pendingLogs.splice(0, pendingLogs.length);
  // Placeholder: POST to /api/v1/logs — implemented in Prompt 8
  void fetch('/api/v1/logs', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(batch),
    keepalive: true,
  }).catch(() => {
    // Silently fail — logging must never crash the app
  });
}

export const logger = {
  debug: (message: string, data?: unknown) => log('debug', message, data),
  info: (message: string, data?: unknown) => log('info', message, data),
  warn: (message: string, data?: unknown) => log('warn', message, data),
  error: (message: string, data?: unknown) => log('error', message, data),
  flush: flushLogs,
};
